import { useContext, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { UserContext } from "../../contexts/UserContext";

function sanitizeStorageName(name = "document.pdf") {
  return String(name || "document.pdf")
    .replace(/[^\w.\- ]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);
}

function formatBytes(bytes = 0) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "-";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "-";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function DocumentsLibrary() {
  const { userInfo } = useContext(UserContext) || {};
  const isAdmin = String(userInfo?.role || "").toLowerCase() === "admin";

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const qy = query(collection(db, "documentFolders"), orderBy("name"));
    return onSnapshot(qy, (snap) => {
      const list = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setFolders(list);
      setSelectedFolderId((current) => current || list[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    const qy = query(collection(db, "documentFiles"), orderBy("uploadedAt", "desc"));
    return onSnapshot(qy, (snap) => {
      setFiles(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    });
  }, []);

  const filesByFolder = useMemo(() => {
    const map = {};
    files.forEach((file) => {
      const id = file.folderId || "uncategorized";
      if (!map[id]) map[id] = [];
      map[id].push(file);
    });
    return map;
  }, [files]);

  const visibleFiles = filesByFolder[selectedFolderId] || [];

  const createFolder = async (event) => {
    event.preventDefault();
    const name = folderName.trim();
    if (!name || !isAdmin) return;

    setBusy(true);
    setMessage("");
    try {
      const folderRef = await addDoc(collection(db, "documentFolders"), {
        name,
        createdAt: serverTimestamp(),
        createdBy: userInfo?.email || userInfo?.uid || "admin",
      });
      setSelectedFolderId(folderRef.id);
      setFolderName("");
      setMessage("Folder created.");
    } catch (error) {
      console.error("Failed to create document folder:", error);
      setMessage("Could not create folder. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const uploadPdf = async (event) => {
    event.preventDefault();
    if (!isAdmin || !selectedFolderId || !uploadFile) return;
    if (uploadFile.type !== "application/pdf" && !/\.pdf$/i.test(uploadFile.name || "")) {
      setMessage("Please choose a PDF file.");
      return;
    }

    const folder = folders.find((item) => item.id === selectedFolderId);
    const safeFileName = sanitizeStorageName(uploadFile.name || "document.pdf");
    const storagePath = `documentLibrary/${selectedFolderId}/${Date.now()}_${safeFileName}`;

    setBusy(true);
    setMessage("");
    try {
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, uploadFile, { contentType: "application/pdf" });
      const url = await getDownloadURL(fileRef);
      await addDoc(collection(db, "documentFiles"), {
        folderId: selectedFolderId,
        folderName: folder?.name || "",
        name: uploadName.trim() || uploadFile.name || "Document.pdf",
        originalName: uploadFile.name || "",
        storagePath,
        url,
        size: uploadFile.size || 0,
        contentType: "application/pdf",
        uploadedAt: serverTimestamp(),
        uploadedBy: userInfo?.email || userInfo?.uid || "admin",
      });
      setUploadFile(null);
      setUploadName("");
      event.target.reset();
      setMessage("PDF uploaded.");
    } catch (error) {
      console.error("Failed to upload PDF:", error);
      setMessage("Could not upload PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (file) => {
    if (!isAdmin || !file?.id) return;
    if (!window.confirm(`Delete "${file.name || "this PDF"}"?`)) return;

    setBusy(true);
    setMessage("");
    try {
      if (file.storagePath) {
        try {
          await deleteObject(ref(storage, file.storagePath));
        } catch (error) {
          console.warn("Storage file delete skipped:", error);
        }
      }
      await deleteDoc(doc(db, "documentFiles", file.id));
      setMessage("PDF deleted.");
    } catch (error) {
      console.error("Failed to delete PDF:", error);
      setMessage("Could not delete PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-baseline gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
            Documents
          </span>
          <h1 className="text-lg font-semibold tracking-tight text-gray-950">PDF folders</h1>
        </div>
        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{files.length}</span> files
        </div>
      </div>

      {message ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 shadow-sm">
          {message}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-[24px] border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-4">
            <h2 className="text-base font-semibold text-gray-900">Folders</h2>
          </div>

          {isAdmin ? (
            <form onSubmit={createFolder} className="border-b border-gray-100 p-4">
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                New folder
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  value={folderName}
                  onChange={(event) => setFolderName(event.target.value)}
                  placeholder="Policies"
                  className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                />
                <button
                  type="submit"
                  disabled={!folderName.trim() || busy}
                  className="rounded-xl bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-black disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </form>
          ) : null}

          <div className="max-h-[540px] overflow-y-auto p-2">
            {folders.length ? (
              folders.map((folder) => {
                const active = selectedFolderId === folder.id;
                const count = filesByFolder[folder.id]?.length || 0;
                return (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => setSelectedFolderId(folder.id)}
                    className={`mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition ${
                      active
                        ? "bg-emerald-50 text-emerald-800"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="min-w-0 truncate font-medium">{folder.name}</span>
                    <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs text-gray-500">
                      {count}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                {isAdmin ? "Create a folder to start uploading PDFs." : "No folders yet."}
              </div>
            )}
          </div>
        </aside>

        <main className="rounded-[24px] border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {folders.find((folder) => folder.id === selectedFolderId)?.name || "Select a folder"}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Open a PDF in a new tab or download it from your browser.
                </p>
              </div>

              {isAdmin && selectedFolderId ? (
                <form onSubmit={uploadPdf} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <input
                    value={uploadName}
                    onChange={(event) => setUploadName(event.target.value)}
                    placeholder="Display name"
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                  />
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm file:mr-3 file:rounded-lg file:border-0 file:bg-gray-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
                  />
                  <button
                    type="submit"
                    disabled={!uploadFile || busy}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Upload
                  </button>
                </form>
              ) : null}
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {visibleFiles.length ? (
              visibleFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">{file.name || file.originalName}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span>{formatBytes(file.size)}</span>
                      <span>Uploaded {formatDate(file.uploadedAt)}</span>
                      {file.uploadedBy ? <span>{file.uploadedBy}</span> : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 no-underline shadow-sm transition hover:bg-gray-50"
                    >
                      Open PDF
                    </a>
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => removeFile(file)}
                        disabled={busy}
                        className="rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 shadow-sm transition hover:bg-red-50 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-12 text-center text-sm text-gray-500 sm:px-5">
                {selectedFolderId ? "No PDFs in this folder yet." : "Select a folder to view PDFs."}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
